import { useState, useRef, useMemo, useEffect } from 'react';
import useDealStore from '../store/dealStore';
import { recordOMStatus } from '../services/idleNotificationService';
import { parseDealDocument } from '../services/documentParsingService';
import { calculateSdaIncomeAndNoi, maxZero } from '../utils/sdaFormulaCalculator';
import { formatFieldSources, getParsedFieldSources } from '../utils/documentSourceUtils';
import { TwoMinuteAnalysis } from '../components/dealIntake/TwoMinuteAnalysis';
import '../styles/module3.css';

// Task 2: Expense ratio cutoff constants (never change these to strings)
const EXPENSE_RATIO_MIN = 0.45
const EXPENSE_RATIO_MAX = 0.50

const DOC_TYPES = [
  { id: 'om', label: 'OM', sublabel: 'Offering Memo', icon: '📄', accept: '.pdf,.txt', color: '#0f2d5e', bg: '#eff6ff' },
  { id: 'rentRoll', label: 'Rent Roll', sublabel: 'XLSX / PDF', icon: '📊', accept: '.xlsx,.xls,.pdf', color: '#1d4ed8', bg: '#eff6ff' },
  { id: 't12', label: 'T12 / P&L', sublabel: 'Financials', icon: '📈', accept: '.xlsx,.xls,.pdf', color: '#2563eb', bg: '#eff6ff' },
  { id: 'photos', label: 'Photos', sublabel: 'JPG / PNG', icon: '🖼️', accept: '.jpg,.jpeg,.png', color: '#1e3a8a', bg: '#eff6ff' },
  { id: 'link', label: 'Link', sublabel: 'Listing URL', icon: '🔗', accept: null, color: '#2563eb', bg: '#eff6ff' },
];

const ASSET_TYPES = ['Multifamily', 'Mixed-Use', 'SFR Portfolio', 'Commercial', 'Other'];
const PARSED_FIELD_LABELS = {
  propertyName: 'Property Name',
  address: 'Address',
  city: 'City',
  state: 'State',
  zip: 'ZIP',
  units: 'Units',
  yearBuilt: 'Year Built',
  occupancy: 'Occupancy',
  vacancyRate: 'Vacancy Rate',
  brokerName: 'Broker Name',
  brokerPhone: 'Broker Phone',
  brokerEmail: 'Broker Email',
  askingPrice: 'Asking Price',
  grossRent: 'Monthly Gross Rent',
  otherIncome: 'Other Income / Mo',
  insurance: 'Insurance (Annual)',
  opExpenses: 'Annual Op. Expenses (excl. insurance)',
  noi: 'NOI',
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const calculateModule3NOI = (grossRentMonthly, otherIncomeMonthly, vacancyRate, annualExpenses) => {
  const result = calculateSdaIncomeAndNoi({
    grossRentMonthly,
    otherIncomeMonthly,
    vacancyRate,
    operatingExpensesAnnual: annualExpenses,
  });
  return result.effectiveGrossIncome > 0 || result.totalOperatingExpenses > 0 ? String(Math.round(result.noi)) : '';
};
const VISIBLE_DEAL_FIELDS = [
  'propertyName',
  'address',
  'city',
  'state',
  'zip',
  'units',
  'assetType',
  'askingPrice',
  'yearBuilt',
  'occupancy',
  'brokerName',
  'brokerPhone',
  'brokerEmail',
  'grossRent',
  'otherIncome',
  'vacancyRate',
  'insurance',
  'opExpenses',
  'noi',
];

// Default insurance estimate: 0.25% of purchase price annually
const estimateInsurance = (askingPrice) => {
  const price = Number(askingPrice);
  return Number.isFinite(price) && price > 0 ? Math.round(price * 0.0025) : 0;
};

const hasValue = (value) => value !== '' && value != null;
const isMeaningfulValue = (value) => value !== '' && value !== null && value !== undefined;
const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// Fields that contain large numbers but are NOT dollar amounts — must not get $ formatting.
const NON_MONEY_FIELDS = new Set(['zip', 'yearBuilt', 'units', 'occupancy', 'vacancyRate', 'capRate', 'brokerPhone', 'extractionProfile'])

const formatReviewValue = (value, field = '') => {
  if (!isMeaningfulValue(value)) return '—';
  if (NON_MONEY_FIELDS.has(field)) return String(value);
  const numeric = Number(value);
  if (Number.isFinite(numeric) && Math.abs(numeric) >= 1000) return `$${Math.round(numeric).toLocaleString()}`;
  return String(value);
};
const buildHighlightedPreview = (text = '', terms = []) => {
  const source = String(text || '');
  const cleanTerms = [...new Set(terms.map(term => String(term || '').trim()).filter(term => term.length >= 3))].slice(0, 60);
  if (!source || cleanTerms.length === 0) return source ? [source] : ['No source text preview available.'];
  const pattern = new RegExp(`(${cleanTerms.map(escapeRegExp).join('|')})`, 'gi');
  return source.split(pattern).filter(Boolean).map((part, index) => {
    const matched = cleanTerms.some(term => term.toLowerCase() === part.toLowerCase());
    return matched ? <mark key={`${part}-${index}`}>{part}</mark> : part;
  });
};

function FileCard({ doc, file, link, locked = false, onFile, onRemove, onLink }) {
  const inputRef = useRef();
  const [drag, setDrag] = useState(false);

  if (doc.id === 'link') {
    return (
      <div className="m3-doc-card" style={{ '--doc-color': doc.color, '--doc-bg': doc.bg }}>
        <div className="m3-doc-icon">{doc.icon}</div>
        <div className="m3-doc-label">{doc.label}</div>
        <div className="m3-doc-sublabel">{doc.sublabel}</div>
        <input
          type="url"
          className="m3-link-input"
          placeholder="https://loopnet.com/…"
          value={link}
          onChange={e => onLink(e.target.value)}
        />
      </div>
    );
  }

  return (
    <div
      className={`m3-doc-card ${drag ? 'drag-over' : ''} ${file ? 'has-file' : ''}`}
      style={{ '--doc-color': doc.color, '--doc-bg': doc.bg }}
      onClick={() => !locked && !file && inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => {
        e.preventDefault(); setDrag(false);
        if (locked) return;
        const f = e.dataTransfer.files[0];
        if (f) onFile(f);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={doc.accept}
        style={{ display: 'none' }}
        onChange={e => {
          if (e.target.files[0]) {
            onFile(e.target.files[0]);
            e.target.value = '';
          }
        }}
      />
      <div className="m3-doc-icon">{file ? '' : doc.icon}</div>
      <div className="m3-doc-label">{doc.label}</div>
      <div className="m3-doc-sublabel">
        {file ? file.name.length > 18 ? file.name.slice(0, 16) + '…' : file.name : doc.sublabel}
      </div>
      {file && (
        <button
          className="m3-doc-remove"
          onClick={e => {
            e.stopPropagation();
            if (onRemove) onRemove();
            else onFile(null);
          }}
          aria-label={`Remove ${doc.label}`}
          title={`Remove ${doc.label}`}
        >
          ×
        </button>
      )}
      {locked ? <div className="m3-doc-hint">Uploaded</div> : !file && <div className="m3-doc-hint">Drop or click</div>}
    </div>
  );
}

function FieldSourceHint({ sources = [], fallback = 'Manual / not extracted from OM, RR, or T12' }) {
  const label = sources.length ? sources.map(source => source.label).join('+') : 'i';
  const title = sources.length ? `Extracted from ${formatFieldSources(sources)}` : fallback;

  return (
    <span className={`m3-source-hint ${sources.length ? 'has-source' : 'no-source'}`} tabIndex={0} aria-label={title}>
      <span className="m3-source-badge">{label}</span>
      <span className="m3-source-popover" role="tooltip">{title}</span>
    </span>
  );
}

function FieldLabel({ children, required = false, sources = [], fallback }) {
  return (
    <label className="m3-label-with-source">
      <span>{children}{required && <span className="req"> *</span>}</span>
      <FieldSourceHint sources={sources} fallback={fallback} />
    </label>
  );
}

function Module3Page() {
  const { dealState, updateDealState, updateAvailableModules, navigateToModule, forceNavigateToModule } = useDealStore();

  const rd = dealState.rawDealData || {};
  const existingFinancials = dealState.extractedFinancials || {};
  const existingRevenue = existingFinancials.revenue || {};
  const existingExpenses = existingFinancials.expenses || {};
  const existingRentRoll = existingRevenue.rentRoll || {};

  // Form state — always start blank, only show values actually extracted from uploaded documents.
  // Never bleed Module-1 pre-fill values (rawDealData) into fields the OM didn't provide.
  const omAlreadySubmitted = !!dealState.financialDocuments?.omUploaded;

  const [form, setForm] = useState(() => {
    const EMPTY = {
      propertyName: '', address: '', city: '', state: '', zip: '',
      units: '', askingPrice: '', assetType: 'Multifamily', yearBuilt: '',
      occupancy: '', brokerName: '', brokerPhone: '', brokerEmail: '',
      grossRent: '', otherIncome: '', vacancyRate: '', insurance: '',
      opExpenses: '', noi: '',
    };

    if (!omAlreadySubmitted) {
      // No OM uploaded yet — completely blank form
      return EMPTY;
    }

    // OM was previously submitted (returning to page / refresh).
    // Restore ONLY from the parsed document fields — not from rawDealData — so we
    // never show Module-1 pre-fills in fields the OM didn't actually extract.
    const storedParsed = dealState.financialDocuments?.parsedDocuments || {};
    const omFields = storedParsed.om?.fields;

    if (!omFields || Object.keys(omFields).length === 0) {
      // Very old session without parsedDocuments — fall back to extractedFinancials only
      const exRev = existingRevenue;
      const exExp = existingExpenses;
      return {
        ...EMPTY,
        propertyName: dealState.dealName || '',
        grossRent: exRev.grossPotentialRent ? String(Math.round(exRev.grossPotentialRent / 12)) : '',
        otherIncome: exRev.otherIncome ? String(Math.round(exRev.otherIncome / 12)) : '',
        occupancy: existingRentRoll.occupancyRate || '',
        vacancyRate: existingRentRoll.occupancyRate ? String(Math.max(0, 100 - Number(existingRentRoll.occupancyRate))) : '',
        opExpenses: exExp.totalOperatingExpenses || exExp.totalExpenses || '',
        noi: existingFinancials.noi || '',
      };
    }

    // Build form from parsed document fields only (each doc applied in order)
    const initial = { ...EMPTY };

    ['om', 'rentRoll', 't12'].forEach(docId => {
      const docFields = storedParsed[docId]?.fields;
      if (!docFields) return;
      VISIBLE_DEAL_FIELDS.forEach(key => {
        const val = docFields[key];
        if (hasValue(val)) {
          const isDealInfoField = [
            'propertyName', 'address', 'city', 'state', 'zip', 'units',
            'assetType', 'askingPrice', 'yearBuilt', 'occupancy',
            'brokerName', 'brokerPhone', 'brokerEmail'
          ].includes(key);
          if (docId === 'om' || !isDealInfoField) {
            initial[key] = String(val);
          }
        }
      });
    });

    // Derive occupancy ↔ vacancyRate cross-fill
    if (hasValue(initial.vacancyRate) && !hasValue(initial.occupancy)) {
      const vac = Number(initial.vacancyRate);
      if (Number.isFinite(vac)) initial.occupancy = String(Math.max(0, Math.round(100 - vac)));
    }
    if (hasValue(initial.occupancy) && !hasValue(initial.vacancyRate)) {
      const occ = Number(initial.occupancy);
      if (Number.isFinite(occ)) initial.vacancyRate = String(Math.max(0, 100 - occ));
    }

    // Restore financial values from extractedFinancials if parsedDocuments didn't give them
    if (!initial.grossRent && existingRevenue.grossPotentialRent)
      initial.grossRent = String(Math.round(existingRevenue.grossPotentialRent / 12));
    if (!initial.opExpenses && existingExpenses.totalOperatingExpenses)
      initial.opExpenses = String(existingExpenses.totalOperatingExpenses);
    if (!initial.noi && existingFinancials.noi)
      initial.noi = String(existingFinancials.noi);

    // Recalculate NOI if we got the inputs but not the NOI itself
    if (!initial.noi && (initial.grossRent || initial.opExpenses))
      initial.noi = calculateModule3NOI(initial.grossRent, initial.otherIncome, initial.vacancyRate, initial.opExpenses);

    return initial;
  });

  // Task 4: Negotiation fields state

  // Task 2: Expense ratio popup state
  const [expenseRatioPopup, setExpenseRatioPopup] = useState(null) // { ratio, message }

  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');

  const [files, setFiles] = useState({
    om: dealState.financialDocuments?.omUploaded
      ? { name: dealState.financialDocuments?.omFileName || 'Submitted OM' }
      : null,
    rentRoll: dealState.financialDocuments?.rentRollUploaded
      ? { name: dealState.financialDocuments?.rentRollFileName || 'Submitted Rent Roll' }
      : null,
    t12: dealState.financialDocuments?.t12Uploaded
      ? { name: dealState.financialDocuments?.t12FileName || 'Submitted T12' }
      : null,
    photos: null,
  });
  const [omLocked, setOmLocked] = useState(!!dealState.financialDocuments?.omUploaded);
  const [parseStatus, setParseStatus] = useState(dealState.financialDocuments?.parsedDocuments || {});
  const [listingLink, setListingLink] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [validationPopup, setValidationPopup] = useState(null); // list of missing field labels
  const [docErrorPopup, setDocErrorPopup] = useState(null);    // { label, message } for wrong-doc rejection
  const [extractionWarnings, setExtractionWarnings] = useState(null); // { issues: [{field, extracted, reason}], fileName }
  const [extractionDrawerOpen, setExtractionDrawerOpen] = useState(false);
  // ── Document upload tracking ──────────────────────────────────────────────
  // Whenever any file is set (or cleared), sync to the store + idle tracker.
  useEffect(() => {
    const isOmUploaded = omLocked || !!files.om;
    recordOMStatus(isOmUploaded);

    const isRentRollUploaded = !!files.rentRoll;
    const isT12Uploaded = !!files.t12;

    updateDealState({
      financialDocuments: {
        ...(dealState.financialDocuments || {}),
        omUploaded: isOmUploaded,
        omFileName: isOmUploaded ? (files.om?.name || dealState.financialDocuments?.omFileName || null) : null,
        rentRollUploaded: isRentRollUploaded,
        rentRollFileName: isRentRollUploaded ? (files.rentRoll?.name || dealState.financialDocuments?.rentRollFileName || null) : null,
        t12Uploaded: isT12Uploaded,
        t12FileName: isT12Uploaded ? (files.t12?.name || dealState.financialDocuments?.t12FileName || null) : null,
        parsedDocuments: parseStatus,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.om, files.rentRoll, files.t12, omLocked, parseStatus]);

  // (RentCast rent-estimate hook removed — feature no longer wired to any API)

  // ── Feature 1: Missing Data Detector ──────────────────────────────────────
  // Checks required + recommended fields in real-time and produces a quality score.
  const dataQuality = useMemo(() => {
    const checks = [
      // Required
      { key: 'propertyName', label: 'Property Name', cat: 'required', done: !!form.propertyName.trim() },
      { key: 'address', label: 'Street Address', cat: 'required', done: !!form.address.trim() },
      { key: 'city', label: 'City', cat: 'required', done: !!form.city.trim() },
      { key: 'units', label: 'Unit Count', cat: 'required', done: !!form.units },
      { key: 'askingPrice', label: 'Asking Price', cat: 'required', done: !!form.askingPrice },
      // Financials
      { key: 'occupancy', label: 'Occupancy Rate', cat: 'financial', done: !!form.occupancy },
      { key: 'grossRent', label: 'Monthly Gross Rent', cat: 'financial', done: !!form.grossRent },
      { key: 'opExpenses', label: 'Operating Expenses', cat: 'financial', done: !!form.opExpenses },
      { key: 'yearBuilt', label: 'Year Built', cat: 'financial', done: !!form.yearBuilt },
      // Broker
      { key: 'brokerName', label: 'Broker Name', cat: 'broker', done: !!form.brokerName.trim() },
      { key: 'brokerEmail', label: 'Broker Email', cat: 'broker', done: !!form.brokerEmail.trim() },
      // Documents
      { key: 'doc_om', label: 'Offering Memo (OM)', cat: 'document', done: !!files.om },
      { key: 'doc_rentRoll', label: 'Rent Roll', cat: 'document', done: !!files.rentRoll },
      { key: 'doc_t12', label: 'T12 / P&L', cat: 'document', done: !!files.t12 },
    ];
    const done = checks.filter(c => c.done).length;
    const pct = Math.round((done / checks.length) * 100);
    const level = pct >= 90 ? 'excellent' : pct >= 70 ? 'good' : pct >= 50 ? 'fair' : 'poor';
    return { checks, done, total: checks.length, pct, level };
  }, [form, files]);

  const intakeReadiness = useMemo(() => {
    const missing = dataQuality.checks.filter(c => !c.done);
    const requiredMissing = missing.filter(c => c.cat === 'required').length;
    const docMissing = missing.filter(c => c.cat === 'document').length;
    const nextActions = missing.slice(0, 4).map(c => c.label);
    return {
      label: requiredMissing ? 'Required gaps' : docMissing ? 'Document gaps' : dataQuality.pct >= 90 ? 'Underwriting ready' : 'Review optional gaps',
      nextActions,
      requiredMissing,
      docMissing,
    };
  }, [dataQuality]);

  const setField = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: '' }));
  };

  // SDA formula pattern: NOI = Effective Gross Income - Total Expenses.
  const calcNOI = (gr, oi, vac, op) => {
    return calculateModule3NOI(gr, oi, vac, op);
  };

  // extractedNOI: the NOI value that came directly from the uploaded documents.
  // We track it separately so manual edits to financial inputs don't silently
  // overwrite the broker/actuals figure the user uploaded.
  const extractedNOI = (() => {
    const storedParsed = dealState.financialDocuments?.parsedDocuments || {};
    for (const docId of ['t12', 'om', 'rentRoll']) {
      const noi = storedParsed[docId]?.fields?.noi;
      if (noi) return String(noi);
    }
    return null;
  })();

  // If a document provided a NOI, show that.
  // Only fall back to the live formula when no document NOI is available.
  const displayedNOI = extractedNOI || form.noi || calcNOI(form.grossRent, form.otherIncome, form.vacancyRate, form.opExpenses);

  const extractedFieldKeys = useMemo(() => {
    const keys = new Set();
    Object.values(parseStatus || {}).forEach(status => {
      Object.entries(status?.fields || {}).forEach(([key, value]) => {
        if (isMeaningfulValue(value)) keys.add(key);
      });
    });
    return keys;
  }, [parseStatus]);

  const extractedFieldClass = (key) => extractedFieldKeys.has(key) ? 'm3-input-extracted' : '';
  const sourcesFor = (key) => getParsedFieldSources(parseStatus, key);

  const extractionReview = useMemo(() => {
    const docs = Object.entries(parseStatus || {})
      .filter(([, status]) => status)
      .map(([id, status]) => {
        const extracted = Object.entries(status.fields || {}).filter(([, value]) => isMeaningfulValue(value));
        const terms = extracted.flatMap(([field, value]) => [PARSED_FIELD_LABELS[field] || field, value]);
        return {
          id,
          label: DOC_TYPES.find(doc => doc.id === id)?.label || id,
          state: status.state || 'done',
          fileName: status.fileName || files[id]?.name || dealState.financialDocuments?.[`${id}FileName`] || '',
          message: status.message || '',
          fields: extracted,
          textPreview: status.extractedText || status.textPreview || '',
          terms,
        };
      });

    const calculation = calculateSdaIncomeAndNoi({
      grossRentMonthly: form.grossRent,
      otherIncomeMonthly: form.otherIncome,
      vacancyRate: form.vacancyRate,
      operatingExpensesAnnual: form.opExpenses,
      parsedNoi: extractedNOI || form.noi,
      trustParsedNoi: !!(extractedNOI || form.noi),
    });

    const calculated = [
      ['Gross Potential Rent', calculation.grossPotentialRentAnnual],
      ['Other Income', calculation.otherIncomeAnnual],
      ['Vacancy Loss', calculation.vacancyLoss],
      ['Effective Gross Income', calculation.effectiveGrossIncome],
      ['Operating Expenses', calculation.totalOperatingExpenses],
      ['NOI', displayedNOI || calculation.noi],
      ['NOI Margin', calculation.noiMargin != null ? `${(calculation.noiMargin * 100).toFixed(1)}%` : null],
      ['Expense Ratio', calculation.expenseRatio != null ? `${(calculation.expenseRatio * 100).toFixed(1)}%` : null],
    ].filter(([, value]) => isMeaningfulValue(value));

    const allTerms = docs.flatMap(doc => doc.terms).concat(calculated.map(([label, value]) => [label, value]).flat());

    return {
      docs,
      calculated,
      allTerms,
      hasLogs: docs.length > 0 || isProcessing,
    };
  }, [parseStatus, form, displayedNOI, extractedNOI, files, dealState.financialDocuments, isProcessing]);

  useEffect(() => {
    if (isProcessing || extractionReview.docs.length > 0) setExtractionDrawerOpen(true);
  }, [isProcessing, extractionReview.docs.length]);

  const handleFinancialChange = (key, val) => {
    setField(key, val);
    // Only recalculate NOI from inputs when no document-extracted NOI is present.
    // This prevents manual edits to gross rent / vacancy / expenses from
    // silently overwriting the figure that came out of the T12 or OM.
    if (!extractedNOI) {
      const updated = { ...form, [key]: val };
      const noi = calcNOI(updated.grossRent, updated.otherIncome, updated.vacancyRate, updated.opExpenses);
      setForm(f => ({ ...f, [key]: val, noi }));
    }
  };

  const applyParsedFields = (fields = {}) => {
    setForm(prev => {
      const next = { ...prev };

      VISIBLE_DEAL_FIELDS.forEach((key) => {
        const value = fields[key];
        if (hasValue(value)) {
          next[key] = String(value);
        }
      });

      if (hasValue(fields.vacancyRate) && !hasValue(fields.occupancy)) {
        const vacancy = Number(fields.vacancyRate);
        if (Number.isFinite(vacancy)) next.occupancy = String(Math.max(0, Math.round(100 - vacancy)));
      }

      if (hasValue(next.occupancy) && !hasValue(next.vacancyRate)) {
        const occupancy = Number(next.occupancy);
        if (Number.isFinite(occupancy)) next.vacancyRate = String(Math.max(0, 100 - occupancy));
      }

      if (fields.noi || fields.grossRent || fields.otherIncome || fields.opExpenses || fields.vacancyRate) {
        const formulaNOI = calcNOI(next.grossRent, next.otherIncome, next.vacancyRate, next.opExpenses);
        next.noi = fields.noi ? String(maxZero(fields.noi)) : formulaNOI;
      }

      setErrors(current => {
        const cleared = { ...current };
        ['propertyName', 'address', 'units', 'askingPrice'].forEach((key) => {
          if (hasValue(next[key])) delete cleared[key];
        });
        return cleared;
      });

      return next;
    });
  };

  const buildDealStateUpdatesFromForm = (sourceForm, nextParseStatus = parseStatus, omMeta = {}) => {
    const vacancyPct = toNumber(sourceForm.vacancyRate) || Math.max(0, 100 - (toNumber(sourceForm.occupancy) || 91));
    const income = calculateSdaIncomeAndNoi({
      units: sourceForm.units,
      grossRentMonthly: sourceForm.grossRent,
      otherIncomeMonthly: sourceForm.otherIncome,
      vacancyRate: vacancyPct,
      operatingExpensesAnnual: sourceForm.opExpenses,
      parsedNoi: sourceForm.noi,
      trustParsedNoi: true,
    });
    const grossPotentialRent = income.grossPotentialRentAnnual;
    const otherIncomeAnnual = income.otherIncomeAnnual;
    const totalRevenue = income.totalRevenue;
    const opExp = income.totalOperatingExpenses;
    const noi = income.noi;
    const occupancy = toNumber(sourceForm.occupancy) || Math.max(0, 100 - vacancyPct);
    const askingPrice = toNumber(sourceForm.askingPrice) || rd.price || 0;
    const units = parseInt(sourceForm.units) || 0;
    const estimatedAnnualDebtService = askingPrice ? askingPrice * 0.7 * 0.078 : 0;
    const annualCashFlow = maxZero(noi - estimatedAnnualDebtService);
    const capRate = askingPrice ? maxZero(noi / askingPrice) : 0;

    const insuranceAnnual = toNumber(sourceForm.insurance) || estimateInsurance(askingPrice)
    const insuranceIsEstimated = !toNumber(sourceForm.insurance)

    return {
      dealName: sourceForm.propertyName,
      dealId: dealState.dealId || `deal_${Date.now()}`,
      rawDealData: {
        ...rd,
        source: rd.source || 'manual_upload',
        price: askingPrice,
        location: { address: sourceForm.address, city: sourceForm.city, state: sourceForm.state, zip: sourceForm.zip },
        property: {
          units,
          buildingType: sourceForm.assetType,
          yearBuilt: parseInt(sourceForm.yearBuilt) || null,
          squareFeet: rd.property?.squareFeet,
        },
        dealLeadGenerationDetails: {
          ...(rd.dealLeadGenerationDetails || {}),
          brokerName: sourceForm.brokerName,
          listingUrl: listingLink || rd.dealLeadGenerationDetails?.listingUrl,
          phone: sourceForm.brokerPhone,
          email: sourceForm.brokerEmail,
          contactInfo: sourceForm.brokerPhone || sourceForm.brokerEmail,
        },
      },
      // Clear prior underwriting metrics so Module 5 does not score this new deal
      // using metrics that were computed for a different OM / deal session.
      underwritingMetrics: null,
      // Clear prior score so Module 5 starts fresh
      scoreAndOffer: null,
      financialDocuments: {
        ...(dealState.financialDocuments || {}),
        omUploaded: true,
        omLocked: true,
        omFileName: omMeta.fileName || files.om?.name || dealState.financialDocuments?.omFileName || null,
        omSubmittedAt: omMeta.submittedAt || dealState.financialDocuments?.omSubmittedAt || new Date().toISOString(),
        parsedDocuments: nextParseStatus,
        extractionSource: 'Auto-filled from uploaded OM/document extraction',
      },
      // Populate shared ownership costs from intake form so downstream modules
      // have sensible values before full underwriting runs.
      ownershipCosts: {
        annualInsurance: insuranceAnnual,
        monthlyInsurance: Math.round(insuranceAnnual / 12),
        insuranceIsEstimated,
        annualPropertyOpEx: opExp,
        monthlyPropertyOpEx: Math.round(opExp / 12),
        monthlyPropertyTax: null,
        annualPropertyTax: null,
        monthlyDebtService: null,
        annualDebtService: null,
        totalMonthlyOwnershipCost: null,
        totalAnnualOwnershipCost: null,
      },
      extractedFinancials: {
        revenue: {
          grossPotentialRent,
          otherIncome: otherIncomeAnnual,
          totalRevenue,
          // Persist vacancyRate (as a decimal) so Module 4 uses the actual OM
          // vacancy rather than re-deriving it from occupancy (which ignores
          // other-rental-losses and loss-to-lease deductions).
          vacancyRate: income.vacancyRate,
          rentRoll: {
            totalUnits: units,
            occupiedUnits: Math.round(units * (occupancy / 100)),
            occupancyRate: occupancy,
            averageRent: grossPotentialRent / 12 / (units || 1),
            unitMix: [],
          },
        },
        expenses: {
          operatingExpenses: {
            insurance: insuranceAnnual,
          },
          totalOperatingExpenses: opExp,
          capitalExpenditures: 0,
          totalExpenses: opExp,
        },
        noi,
        dataValidation: { missingFields: [], alerts: [], dataQuality: 'high', completeness: 100 },
        marketIntelligenceDetails: { marketRent: null, marketOccupancy: occupancy, comparableProperties: [], marketTrends: null },
      },
      financialModeling: {
        ...dealState.financialModeling,
        cashFlow: {
          monthlyCashFlow: Math.round(annualCashFlow / 12),
          annualCashFlow: Math.round(annualCashFlow),
          cashOnCashReturn: askingPrice ? maxZero(annualCashFlow / (askingPrice * 0.3)) : 0,
        },
        incomeVsExpenses: {
          revenueBreakdown: {
            grossRent: grossPotentialRent,
            otherIncome: otherIncomeAnnual,
          },
          expenseBreakdown: {
            operatingExpenses: opExp,
            estimatedDebtService: Math.round(estimatedAnnualDebtService),
          },
          profitMargin: totalRevenue ? maxZero(noi / totalRevenue) : 0,
        },
        scenarios: {
          baseCase: { noi, cashFlow: Math.round(annualCashFlow), capRate, irr: 0.12 },
          bestCase: { noi: Math.round(noi * 1.08), cashFlow: Math.round(annualCashFlow * 1.15), capRate: capRate * 1.08, irr: 0.16 },
          worstCase: { noi: Math.round(noi * 0.88), cashFlow: Math.round(annualCashFlow * 0.75), capRate: capRate * 0.88, irr: 0.08 },
        },
        assumptions: {
          vacancyRate: 100 - occupancy,
          rentGrowthRate: 0.03,
          expenseGrowthRate: 0.025,
          capExRate: 0.02,
        },
      },
    };
  };

  const persistOmSubmission = async ({ fileName, fields, sourceForm, status, textPreview }) => {
    const calculated = {
      ...calculateSdaIncomeAndNoi({
        grossRentMonthly: sourceForm.grossRent,
        otherIncomeMonthly: sourceForm.otherIncome,
        vacancyRate: sourceForm.vacancyRate,
        operatingExpensesAnnual: sourceForm.opExpenses,
        parsedNoi: sourceForm.noi,
        trustParsedNoi: true,
      }),
    };

    try {
      await fetch('/api/module3/om-submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealId: dealState.dealId,
          fileName,
          fields,
          calculated,
          textPreview,
        }),
      });
    } catch {
      localStorage.setItem('aq_module3_om_submission', JSON.stringify({
        dealId: dealState.dealId,
        fileName,
        fields,
        calculated,
        textPreview,
        status,
        submittedAt: new Date().toISOString(),
        locked: true,
      }));
    }
  };

  // ── Extraction sanity check ───────────────────────────────────────────────
  // Detects values that are technically non-empty but clearly wrong, e.g.
  //  units="050"  (partial match inside "1,050 units"),
  //  askingPrice=330  (matched "330" instead of "$3,300,000").
  const checkExtractionWarnings = (fields, fileName) => {
    const issues = [];

    // Units: present but starts with 0 (= "050" style partial match)
    const u = String(fields.units ?? '').trim();
    if (u && u.startsWith('0') && u.length > 1) {
      issues.push({
        field: 'Units',
        extracted: u,
        reason: `"${u}" looks like a partial match (e.g. last 3 digits of "1,${u}"). Check the PDF and enter the correct number.`,
      });
    }

    // Asking price: present but suspiciously small for any multifamily deal
    const ap = Number(fields.askingPrice);
    if (fields.askingPrice !== '' && fields.askingPrice != null && ap < 100_000) {
      issues.push({
        field: 'Asking Price',
        extracted: `$${ap.toLocaleString()}`,
        reason: `$${ap.toLocaleString()} is too low for a multifamily deal — the parser likely matched a partial number. Check the OM and correct it.`,
      });
    }
    if (!fields.askingPrice && fields.askingPrice !== 0) {
      issues.push({
        field: 'Asking Price',
        extracted: '(not found)',
        reason: 'Could not find the listing price in this OM. Enter it manually.',
      });
    }

    // NOI: missing
    if (!fields.noi && fields.noi !== 0) {
      issues.push({
        field: 'NOI',
        extracted: '(not found)',
        reason: 'Net Operating Income was not extracted. If the OM has a financial summary page, it may use an unusual label. Enter it manually.',
      });
    }

    // Gross rent: missing
    if (!fields.grossRent && fields.grossRent !== 0) {
      issues.push({
        field: 'Gross Rent',
        extracted: '(not found)',
        reason: 'Monthly gross / scheduled rent was not found. Enter it manually or the NOI calculation will be off.',
      });
    }

    if (issues.length > 0) {
      setExtractionWarnings({ issues, fileName });
    }
  };

  const setFile = async (id, f) => {
    if (isProcessing) return;
    if (id === 'om' && omLocked) return;
    if (id === 'om' && f && errors.om) {
      setErrors(e => ({ ...e, om: '' }));
    }
    setFiles(prev => ({ ...prev, [id]: f }));

    if (!f || id === 'photos') {
      setParseStatus(prev => ({ ...prev, [id]: null }));
      if (id === 'om') setOmLocked(false);
      return;
    }

    const docLabel = DOC_TYPES.find(d => d.id === id)?.label || id;
    setIsProcessing(true);
    setProcessingMessage(`Uploading and parsing ${docLabel} document... Please wait.`);

    setParseStatus(prev => ({
      ...prev,
      [id]: {
        state: 'loading',
        message: `Reading ${f.name}...`,
        fileName: f.name,
        fields: {},
        steps: ['Upload received', 'Reading document text', 'Searching for known underwriting terms'],
      },
    }));

    try {
      const parsed = await parseDealDocument(f, id);

      // ── Document validation: reject the file if it doesn't match the expected type
      if (parsed?.validationResult && !parsed.validationResult.isValid) {
        setFiles(prev => ({ ...prev, [id]: null }));
        // Clear any loading entry so the extraction panel stays clean
        setParseStatus(prev => ({ ...prev, [id]: null }));
        // Show popup modal with the reason
        const docLabel = DOC_TYPES.find(d => d.id === id)?.label || id;
        setDocErrorPopup({ label: docLabel, message: parsed.validationResult.message });
        return;
      }
      const nextStatus = {
        ...parseStatus,
        [id]: {
          state: parsed?.supported === false ? 'limited' : 'done',
          message: parsed?.message || 'Document read complete.',
          fileName: f.name,
          fields: parsed?.fields || {},
          textPreview: parsed?.textPreview || '',
          extractedText: (parsed?.extractedText || parsed?.textPreview || '').slice(0, 5000),
          steps: [
            'Upload received',
            parsed?.supported === false ? 'Limited parser support' : 'Text extraction complete',
            `${Object.values(parsed?.fields || {}).filter(value => isMeaningfulValue(value)).length} fields mapped into MVP`,
            'SDA formulas recalculated from reviewed values',
          ],
        },
      };
      setParseStatus(nextStatus);

      // Surface multi-property and insurance-estimate warnings
      if (parsed?.multiplePropertiesDetected) {
        setDocErrorPopup({
          label: DOC_TYPES.find(d => d.id === id)?.label || id,
          message: 'Multiple properties detected in this file. Only the first property\'s data was extracted. For accurate results, upload a single-property file.',
        });
      } else if (parsed?.fields?.insuranceEstimated) {
        console.info('[Insurance imputed from benchmark]', parsed.fields.insurance, 'annual');
      }

      // ── Distressed T12 detection ──────────────────────────────────────────
      // If T12 NOI / units < $1,000/unit/year the T12 reflects a severely
      // distressed operating period (e.g. bad debt exceeding NOI).
      // Using this as stabilised NOI will produce a nonsensical implied
      // purchase price — warn the user immediately so they don't present
      // wrong numbers to investors.
      if (id === 't12' && parsed?.fields?.noi) {
        const t12Noi   = Number(parsed.fields.noi)
        // NOTE: use `form` (current state), NOT nextFormVal — nextFormVal is
        // declared with `let` further down, so referencing it here throws
        // "Cannot access before initialization" the moment a T12 with NOI is
        // uploaded (this was the production xlsx-upload crash).
        const unitCnt  = Number(parsed?.fields?.units || form?.units || 0)
        const noiPerUnit = unitCnt > 0 ? t12Noi / unitCnt : t12Noi / 50 // fallback 50-unit estimate
        if (t12Noi > 0 && noiPerUnit < 1000) {
          setDocErrorPopup({
            label: 'T12 — Distressed Year Detected',
            message:
              `T12 NOI is $${t12Noi.toLocaleString()} ($${Math.round(noiPerUnit).toLocaleString()}/unit/yr) — ` +
              `this is 10–20× below a normal stabilised multifamily NOI ($2,000–$8,000/unit/yr). ` +
              `This T12 period appears severely distressed (e.g. bad debt or vacancy spike). ` +
              `The implied purchase price derived from this NOI will be unrealistically low. ` +
              `Please enter the Asking Price and a stabilised NOI manually before underwriting.`,
          })
        }
      }

      let nextFormVal = {};
      setForm(prev => {
        // OM upload: start from blank so ONLY the OM's extracted values show.
        // If the OM doesn't have a field, the user sees an empty input — not the
        // Module-1 pre-fill bleeding through as a blue placeholder.
        // Non-OM docs (Rent Roll, T12): build on top of existing form.
        const next = id === 'om' ? { ...BLANK_FORM } : { ...prev };

        // USA CRE data-source priority:
        //   T12  = trailing 12-month actuals → always wins on financials
        //   RR   = current rent roll         → always wins on rent & occupancy
        //   OM   = broker's figures          → wins only on deal info + fills empty slots
        //
        // T12 must override OM financials because OM figures are often pro-forma
        // (optimistic projections), not actuals. Using OM NOI/expenses/rent instead
        // of T12 actuals is the primary cause of wrong underwriting calculations.
        const T12_OVERRIDE = new Set(['grossRent', 'opExpenses', 'noi', 'otherIncome', 'insurance', 'vacancyRate'])
        const RR_OVERRIDE  = new Set(['grossRent', 'occupancy', 'vacancyRate', 'units'])

        VISIBLE_DEAL_FIELDS.forEach((key) => {
          const value = parsed?.fields?.[key];
          if (hasValue(value)) {
            const isDealInfoField = [
              'propertyName', 'address', 'city', 'state', 'zip', 'units',
              'assetType', 'askingPrice', 'yearBuilt', 'occupancy',
              'brokerName', 'brokerPhone', 'brokerEmail'
            ].includes(key);
            const shouldApply =
              id === 'om' ||
              (id === 't12'      && (T12_OVERRIDE.has(key) || !hasValue(next[key]))) ||
              (id === 'rentRoll' && (RR_OVERRIDE.has(key)  || !hasValue(next[key]))) ||
              (!isDealInfoField  && !hasValue(next[key]));  // fallback: fill empty slots
            if (shouldApply) {
              next[key] = String(value);
            }
          }
        });
        if (hasValue(parsed?.fields?.vacancyRate) && !hasValue(parsed?.fields?.occupancy)) {
          const vacancy = Number(parsed.fields.vacancyRate);
          if (Number.isFinite(vacancy)) next.occupancy = String(Math.max(0, Math.round(100 - vacancy)));
        }
        if (hasValue(next.occupancy) && !hasValue(next.vacancyRate)) {
          const occupancy = Number(next.occupancy);
          if (Number.isFinite(occupancy)) next.vacancyRate = String(Math.max(0, 100 - occupancy));
        }
        next.noi = parsed?.fields?.noi
          ? String(maxZero(parsed.fields.noi))
          : calcNOI(next.grossRent, next.otherIncome, next.vacancyRate, next.opExpenses) || next.noi;

        // ── True GPR derivation for T12 ───────────────────────────────────────
        // Some T12s only have "Rent Income" (actual collected, post-vacancy),
        // not a "Gross Potential Rent" row. Using collected rent as GPR then
        // applying vacancy again double-counts vacancy and understates EGI.
        // Fix: if T12 has NOI + vacancyRate + grossRent, derive true GPR:
        //   true GPR = grossRent (collected) / (1 - vacancyRate/100)
        // Only apply when: T12 uploaded, both fields present, and the NOI
        // back-check suggests grossRent is post-vacancy.
        if (id === 't12' && next.grossRent && next.vacancyRate && next.noi) {
          const collectedMonthly = toNumber(next.grossRent)
          const vacPct = toNumber(next.vacancyRate)
          if (collectedMonthly > 0 && vacPct > 0 && vacPct < 60) {
            const trueGprMonthly = Math.round(collectedMonthly / (1 - vacPct / 100))
            // Only upgrade if the result is meaningfully larger (i.e. collected was post-vacancy)
            // and is consistent with NOI (EGI should >= NOI + expenses)
            const annualNOI = toNumber(next.noi)
            const annualExp = toNumber(next.opExpenses) || 0
            const impliedEGI = (trueGprMonthly * 12) + (toNumber(next.otherIncome) || 0) * 12
            const noiBased  = (collectedMonthly * 12) + (toNumber(next.otherIncome) || 0) * 12
            // If true GPR gives EGI closer to NOI + expenses, use it
            if (trueGprMonthly > collectedMonthly * 1.02 &&
                Math.abs(impliedEGI - (annualNOI + annualExp)) < Math.abs(noiBased - (annualNOI + annualExp))) {
              next.grossRent = String(trueGprMonthly)
            }
          }
        }

        nextFormVal = next;
        return next;
      });

      if (id === 'om') {
        setOmLocked(true);
        localStorage.removeItem('aq_agent_blocked_step');
        localStorage.removeItem('aq_agent_blocked_reason');
        localStorage.removeItem('aq_agent_step_idx');
        checkExtractionWarnings(parsed?.fields || {}, f.name);
        const updates = buildDealStateUpdatesFromForm(nextFormVal, nextStatus, { fileName: f.name });
        updateDealState(updates);
        updateAvailableModules();
        persistOmSubmission({
          fileName: f.name,
          fields: parsed?.fields || {},
          sourceForm: nextFormVal,
          status: nextStatus[id],
          textPreview: parsed?.textPreview || '',
        });
      }

      // T12 and RR: push updated financial values into dealState so the
      // underwriting engine (which reads dealState.extractedFinancials) uses
      // T12 actuals instead of the OM pro-forma values it was initialised with.
      // USA CRE standard: T12 = source of truth for all financial calculations.
      if (id === 't12' || id === 'rentRoll') {
        const vacancyPct = toNumber(nextFormVal.vacancyRate)
          || Math.max(0, 100 - (toNumber(nextFormVal.occupancy) || 91))
        const t12Income = calculateSdaIncomeAndNoi({
          grossRentMonthly:        nextFormVal.grossRent,
          otherIncomeMonthly:      nextFormVal.otherIncome,
          vacancyRate:             vacancyPct,
          operatingExpensesAnnual: nextFormVal.opExpenses,
          parsedNoi:               nextFormVal.noi,
          trustParsedNoi:          true,
        })
        const t12Units    = parseInt(nextFormVal.units) || dealState?.extractedFinancials?.revenue?.rentRoll?.totalUnits || 0
        const t12Occupancy = toNumber(nextFormVal.occupancy) || Math.max(0, 100 - vacancyPct)
        const t12Insurance = toNumber(nextFormVal.insurance) || 0
        updateDealState({
          extractedFinancials: {
            ...(dealState?.extractedFinancials || {}),
            revenue: {
              ...(dealState?.extractedFinancials?.revenue || {}),
              grossPotentialRent: t12Income.grossPotentialRentAnnual,
              otherIncome:        t12Income.otherIncomeAnnual,
              totalRevenue:       t12Income.totalRevenue,
              vacancyRate:        t12Income.vacancyRate,
              rentRoll: {
                ...(dealState?.extractedFinancials?.revenue?.rentRoll || {}),
                totalUnits:    t12Units,
                occupiedUnits: Math.round(t12Units * (t12Occupancy / 100)),
                occupancyRate: t12Occupancy,
                averageRent:   t12Income.grossPotentialRentAnnual / 12 / (t12Units || 1),
              },
            },
            expenses: {
              ...(dealState?.extractedFinancials?.expenses || {}),
              totalOperatingExpenses: t12Income.totalOperatingExpenses,
              totalExpenses:          t12Income.totalOperatingExpenses,
              operatingExpenses: {
                ...(dealState?.extractedFinancials?.expenses?.operatingExpenses || {}),
                insurance: t12Insurance,
              },
            },
            noi: t12Income.noi,
          },
        })
      }
    } catch (err) {
      setParseStatus(prev => ({
        ...prev,
        [id]: {
          state: 'error',
          message: `Could not parse ${f.name}: ${err.message}`,
          fileName: f.name,
          fields: {},
          steps: ['Upload received', 'Parser failed before field mapping'],
        },
      }));
    } finally {
      setIsProcessing(false);
      setProcessingMessage('');
    }
  };

  // Completely blank form — used when OM is removed
  // (rawDealData in the store was overwritten by the OM submission, so we can't
  //  use it as a "clean" baseline — we must fully reset to empty)
  const BLANK_FORM = {
    propertyName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    units: '',
    askingPrice: '',
    assetType: 'Multifamily',
    yearBuilt: '',
    occupancy: '',
    brokerName: '',
    brokerPhone: '',
    brokerEmail: '',
    grossRent: '',
    otherIncome: '',
    vacancyRate: '',
    insurance: '',
    opExpenses: '',
    noi: '',
  };

  const removeFile = (id) => {
    const newParseStatus = { ...parseStatus };
    delete newParseStatus[id];

    setFiles(prev => ({ ...prev, [id]: null }));
    setParseStatus(newParseStatus);

    if (id === 'om') {
      // ── OM removed: blank ALL form fields immediately ──────────────────────
      // rawDealData was overwritten when the OM was submitted, so we can't use
      // it as a baseline. The user removed the source of these values — clear them.
      setForm({ ...BLANK_FORM });
      setOmLocked(false);
      recordOMStatus(false);
      localStorage.removeItem('aq_module3_om_submission');

      const restoredRawDealData = dealState.initialRawDealData || {
        source: null,
        price: null,
        location: { address: null, city: null, state: null, zip: null },
        property: { units: null, buildingType: null, yearBuilt: null },
      };

      updateDealState({
        dealName: restoredRawDealData.propertyName || dealState.initialRawDealData?.name || '',
        financialDocuments: {
          omUploaded: false,
          omLocked: false,
          omFileName: null,
          omSubmittedAt: null,
          parsedDocuments: newParseStatus,
          extractionSource: null,
        },
        // Wipe extracted financials so downstream modules (UW Engine, etc.) see clean state
        extractedFinancials: {
          revenue: { grossPotentialRent: null, otherIncome: null, totalRevenue: null, rentRoll: null },
          expenses: { totalOperatingExpenses: null, totalExpenses: null, operatingExpenses: {} },
          noi: null,
        },
        // Clear underwriting metrics and score so Module 4/5 start fresh
        underwritingMetrics: null,
        scoreAndOffer: null,
        // Restore rawDealData from initialRawDealData to avoid breaking Stage 1 validation
        rawDealData: restoredRawDealData,
      });
      updateAvailableModules();

    } else {
      // ── Non-OM document removed: rebuild from remaining docs ───────────────
      // Start blank, then re-apply fields from whatever documents remain
      const base = { ...BLANK_FORM };

      ['om', 'rentRoll', 't12'].forEach(docId => {
        if (docId === id) return;
        const docFields = newParseStatus[docId]?.fields;
        if (!docFields) return;
        VISIBLE_DEAL_FIELDS.forEach(key => {
          const val = docFields[key];
          if (hasValue(val)) {
            const isDealInfoField = [
              'propertyName', 'address', 'city', 'state', 'zip', 'units',
              'assetType', 'askingPrice', 'yearBuilt', 'occupancy',
              'brokerName', 'brokerPhone', 'brokerEmail'
            ].includes(key);
            if (docId === 'om' || !isDealInfoField) {
              base[key] = String(val);
            }
          }
        });
        if (hasValue(docFields.vacancyRate) && !hasValue(docFields.occupancy)) {
          const vac = Number(docFields.vacancyRate);
          if (Number.isFinite(vac)) base.occupancy = String(Math.max(0, Math.round(100 - vac)));
        }
      });

      if (hasValue(base.occupancy) && !hasValue(base.vacancyRate)) {
        const occ = Number(base.occupancy);
        if (Number.isFinite(occ)) base.vacancyRate = String(Math.max(0, 100 - occ));
      }

      base.noi = calcNOI(base.grossRent, base.otherIncome, base.vacancyRate, base.opExpenses);
      setForm(base);
    }
  };

  const validate = () => {
    const e = {};
    if (!(files.om || omLocked || dealState.financialDocuments?.omUploaded)) {
      e.om = 'Offering Memorandum (OM) upload is required before underwriting';
    }
    if (!files.t12 && !parseStatus.t12) {
      e.t12 = 'T12 (Trailing 12-Month financials) upload is required before underwriting';
    }
    if (!form.propertyName.trim()) e.propertyName = 'Required';
    if (!form.address.trim()) e.address = 'Required';
    if (!form.units) e.units = 'Required';
    return e;
  };

  const FIELD_LABELS = {
    om:  'Offering Memorandum (OM) — must be uploaded',
    t12: 'T12 / Trailing 12-Month Financials — must be uploaded for accurate NOI',
    propertyName: 'Property Name',
    address: 'Street Address',
    units: 'Number of Units',
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      // Show a prominent popup listing all missing mandatory fields
      setValidationPopup(Object.keys(e).map(k => FIELD_LABELS[k] || k));
      return;
    }
    const vacancyPct = toNumber(form.vacancyRate) || Math.max(0, 100 - (toNumber(form.occupancy) || 91));
    // Use the best available NOI: document-extracted (T12/OM) takes priority over
    // the form field, which takes priority over the live formula.
    const bestNOI = extractedNOI || form.noi || calcNOI(form.grossRent, form.otherIncome, form.vacancyRate, form.opExpenses);
    const income = calculateSdaIncomeAndNoi({
      units: form.units,
      grossRentMonthly: form.grossRent,
      otherIncomeMonthly: form.otherIncome,
      vacancyRate: vacancyPct,
      operatingExpensesAnnual: form.opExpenses,
      parsedNoi: bestNOI,
      trustParsedNoi: !!bestNOI,
    });
    const grossPotentialRent = income.grossPotentialRentAnnual;
    const otherIncomeAnnual = income.otherIncomeAnnual;
    const totalRevenue = income.totalRevenue;
    const opExp = income.totalOperatingExpenses;
    const grossRevenue = grossPotentialRent || totalRevenue || 855000;
    const noi = income.noi;
    const occupancy = toNumber(form.occupancy) || 91;
    const askingPrice = toNumber(form.askingPrice) || rd.price || 0;
    const estimatedAnnualDebtService = askingPrice ? askingPrice * 0.7 * 0.078 : 0;
    const annualCashFlow = maxZero(noi - estimatedAnnualDebtService);
    const capRate = askingPrice ? maxZero(noi / askingPrice) : 0;

    updateDealState({
      dealName: form.propertyName,
      // Bump this timestamp every time Module 3 submits so the underwriting engine
      // knows to re-initialise even when dealId hasn't changed.
      financialsUpdatedAt: Date.now(),
      rawDealData: {
        ...rd,
        price: askingPrice,
        location: { address: form.address, city: form.city, state: form.state, zip: form.zip },
        property: { units: parseInt(form.units), buildingType: form.assetType, yearBuilt: parseInt(form.yearBuilt), squareFeet: rd.property?.squareFeet },
        dealLeadGenerationDetails: { brokerName: form.brokerName, contactInfo: form.brokerPhone || form.brokerEmail },
      },
      financialDocuments: {
        ...(dealState.financialDocuments || {}),
        parsedDocuments: parseStatus,
        extractionSource: Object.values(parseStatus).some(status => status?.state === 'done')
          ? 'Uploaded document text extraction'
          : 'Manual entry / supported parser unavailable',
      },
      extractedFinancials: {
        revenue: {
          grossPotentialRent: grossPotentialRent || grossRevenue,
          otherIncome: otherIncomeAnnual,
          totalRevenue: totalRevenue || grossRevenue,
          vacancyRate: income.vacancyRate,
          rentRoll: {
            totalUnits: parseInt(form.units) || 0,
            occupiedUnits: Math.round((parseInt(form.units) || 0) * (occupancy / 100)),
            occupancyRate: occupancy,
            averageRent: (grossPotentialRent || grossRevenue) / 12 / (parseInt(form.units) || 1),
            unitMix: [],
          },
        },
        expenses: {
          operatingExpenses: {},
          totalOperatingExpenses: opExp,
          capitalExpenditures: 0,
          totalExpenses: opExp,
        },
        noi,
        dataValidation: { missingFields: [], alerts: [], dataQuality: 'high', completeness: 90 },
        marketIntelligenceDetails: { marketRent: null, marketOccupancy: occupancy, comparableProperties: [], marketTrends: null },
      },
      financialModeling: {
        ...dealState.financialModeling,
        cashFlow: {
          monthlyCashFlow: Math.round(annualCashFlow / 12),
          annualCashFlow: Math.round(annualCashFlow),
          cashOnCashReturn: askingPrice ? maxZero(annualCashFlow / (askingPrice * 0.3)) : 0,
        },
        incomeVsExpenses: {
          revenueBreakdown: {
            grossRent: grossPotentialRent || grossRevenue,
            otherIncome: otherIncomeAnnual,
          },
          expenseBreakdown: {
            operatingExpenses: opExp,
            estimatedDebtService: Math.round(estimatedAnnualDebtService),
          },
          profitMargin: grossRevenue ? maxZero(noi / grossRevenue) : 0,
        },
        scenarios: {
          baseCase: {
            noi,
            cashFlow: Math.round(annualCashFlow),
            capRate,
            irr: 0.12,
          },
          bestCase: {
            noi: Math.round(noi * 1.08),
            cashFlow: Math.round(annualCashFlow * 1.15),
            capRate: capRate * 1.08,
            irr: 0.16,
          },
          worstCase: {
            noi: Math.round(noi * 0.88),
            cashFlow: Math.round(annualCashFlow * 0.75),
            capRate: capRate * 0.88,
            irr: 0.08,
          },
        },
        assumptions: {
          vacancyRate: 100 - occupancy,
          rentGrowthRate: 0.03,
          expenseGrowthRate: 0.025,
          capExRate: 0.02,
        },
      },
    });

    // Task 2: Compute expense ratio and flag if outside 45-50%
    const grossRevForRatio = grossPotentialRent || grossRevenue || 1
    const computedExpenseRatio = opExp / grossRevForRatio
    if (computedExpenseRatio < EXPENSE_RATIO_MIN || computedExpenseRatio > EXPENSE_RATIO_MAX) {
      let flagMessage = `Expense Ratio of ${(computedExpenseRatio * 100).toFixed(1)}% is outside the acceptable range of ${(EXPENSE_RATIO_MIN * 100).toFixed(0)}–${(EXPENSE_RATIO_MAX * 100).toFixed(0)}%. Review total expenses and gross revenue before proceeding.`
      try {
        const resp = await fetch('/api/deal-messages/expense_ratio_flag')
        if (resp.ok) { const d = await resp.json(); flagMessage = d.message }
      } catch { }
      setExpenseRatioPopup({ ratio: computedExpenseRatio, message: flagMessage })
    }

    // Task 3: persist module stage
    updateDealState({ moduleStage: 3 });

    updateAvailableModules();
    setSubmitted(true);
    // Task 5: Go to Deal Score (Module 5) before Underwriting (Module 4)
    setTimeout(() => forceNavigateToModule(4), 1200);
  };

  if (submitted) {
    return (
      <div className="m3-success">
        <div className="m3-success-icon"></div>
        <h2>Deal Intake Complete!</h2>
        <p>Financial data saved. Navigating to Underwriting…</p>
      </div>
    );
  }

  return (
    <div className="m3-page">

      {/* ── Processing Overlay ─────────────────────────────────────────── */}
      {isProcessing && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          background: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }} className="m3-processing-overlay">
          <div style={{
            background: 'rgba(30, 41, 59, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 24,
            padding: '48px 40px',
            maxWidth: 480,
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 24,
          }}>
            {/* Spinning Loader */}
            <div style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              border: '4px dashed rgba(59, 130, 246, 0.3)',
              borderTop: '4px solid #3b82f6',
              animation: 'm3-spin 1s linear infinite',
            }} />
            <style>{`
              @keyframes m3-spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              .m3-processing-overlay h3,
              .m3-processing-overlay p,
              .m3-processing-overlay-note {
                color: #ffffff !important;
              }
            `}</style>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <h3 style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 700,
                color: '#ffffff',
                letterSpacing: '-0.025em',
              }}>
                Analyzing Document
              </h3>
              <p style={{
                margin: 0,
                fontSize: 14,
                color: '#ffffff',
                lineHeight: 1.5,
              }}>
                {processingMessage || 'Extracting property financials & details...'}
              </p>
            </div>

            <div style={{
              fontSize: 12,
              color: '#ffffff',
              background: 'rgba(15, 23, 42, 0.4)',
              padding: '8px 16px',
              borderRadius: 12,
              border: '1px solid rgba(255, 255, 255, 0.05)',
            }} className="m3-processing-overlay-note">
              Please do not refresh or close the page.
            </div>
          </div>
        </div>
      )}

      {/* ── Mandatory field validation popup ─────────────────────────────── */}
      {validationPopup && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '32px 36px',
            maxWidth: 440, width: '90%',
            boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 44, marginBottom: 10 }}>⚠️</div>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, color: '#dc2626', fontWeight: 700 }}>
              Required Fields Missing
            </h3>
            <p style={{ margin: '0 0 18px', color: '#64748b', fontSize: 14 }}>
              The following mandatory fields must be filled before advancing to the Underwriting Engine:
            </p>
            <ul style={{
              textAlign: 'left', margin: '0 0 24px',
              padding: '0 0 0 20px', color: '#1e293b',
            }}>
              {validationPopup.map((label, i) => (
                <li key={i} style={{
                  padding: '6px 0', fontSize: 14, fontWeight: 600,
                  borderBottom: i < validationPopup.length - 1 ? '1px solid #f1f5f9' : 'none',
                  listStyle: 'disc',
                }}>
                  {label}
                </li>
              ))}
            </ul>
            <p style={{ margin: '0 0 20px', color: '#94a3b8', fontSize: 13 }}>
              If the OM didn&apos;t extract these values, enter them manually in the form on the right.
            </p>
            <button
              onClick={() => setValidationPopup(null)}
              style={{
                padding: '11px 32px', borderRadius: 8, border: 'none',
                background: '#1B3A6B', color: '#fff', fontWeight: 700,
                cursor: 'pointer', fontSize: 14,
              }}
            >
              OK — I&apos;ll Fill Them In
            </button>
          </div>
        </div>
      )}

      {/* ── Extraction quality warning popup ─────────────────────────────── */}
      {extractionWarnings && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.55)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '32px 36px',
            maxWidth: 500, width: '92%',
            boxShadow: '0 12px 40px rgba(0,0,0,0.26)',
            textAlign: 'left',
          }}>
            <div style={{ fontSize: 40, marginBottom: 8, textAlign: 'center' }}>🔍</div>
            <h3 style={{ margin: '0 0 6px', fontSize: 18, color: '#b45309', fontWeight: 700, textAlign: 'center' }}>
              Extraction Quality Warning
            </h3>
            <p style={{ margin: '0 0 4px', color: '#64748b', fontSize: 13, textAlign: 'center' }}>
              <strong>{extractionWarnings.fileName}</strong>
            </p>
            <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: 13, textAlign: 'center' }}>
              Some extracted values look suspicious. Review and correct them before underwriting — wrong numbers here flow through to every calculation.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {extractionWarnings.issues.map((issue, i) => (
                <div key={i} style={{
                  background: '#fffbeb', border: '1px solid #fcd34d',
                  borderRadius: 8, padding: '10px 14px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{
                      background: '#f59e0b', color: '#fff', borderRadius: 4,
                      padding: '1px 8px', fontSize: 11, fontWeight: 700,
                    }}>
                      {issue.field}
                    </span>
                    <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: '#dc2626' }}>
                      {issue.extracted}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: '#78350f' }}>{issue.reason}</p>
                </div>
              ))}
            </div>
            <p style={{ margin: '0 0 16px', color: '#94a3b8', fontSize: 12, textAlign: 'center' }}>
              Correct values in the form on the right, then proceed to underwriting.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => setExtractionWarnings(null)}
                style={{
                  padding: '11px 28px', borderRadius: 8, border: 'none',
                  background: '#1B3A6B', color: '#fff', fontWeight: 700,
                  cursor: 'pointer', fontSize: 14,
                }}
              >
                Got It — I&apos;ll Fix Them
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Task 2: Expense ratio flag popup ─────────────────────────────── */}
      {expenseRatioPopup && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.55)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '36px 36px 28px',
            maxWidth: 500, width: '92%',
            boxShadow: '0 16px 48px rgba(0,0,0,0.25)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12, lineHeight: 1 }}>⚠️</div>
            <h3 style={{ margin: '0 0 6px', fontSize: 19, color: '#d97706', fontWeight: 700 }}>
              Expense Ratio Flag
            </h3>
            <p style={{ margin: '0 0 8px', color: '#0f172a', fontSize: 15, fontWeight: 600 }}>
              Computed Expense Ratio: {(expenseRatioPopup.ratio * 100).toFixed(1)}%
            </p>
            <p style={{ margin: '0 0 8px', color: '#64748b', fontSize: 13 }}>
              Target Range: {(EXPENSE_RATIO_MIN * 100).toFixed(0)}% – {(EXPENSE_RATIO_MAX * 100).toFixed(0)}%
            </p>
            <p style={{
              margin: '0 0 24px', color: '#64748b', fontSize: 14,
              background: '#fffbeb', border: '1px solid #f59e0b',
              borderRadius: 8, padding: '12px 16px', lineHeight: 1.6,
            }}>
              {expenseRatioPopup.message}
            </p>
            <button
              onClick={() => setExpenseRatioPopup(null)}
              style={{
                padding: '11px 36px', borderRadius: 8, border: 'none',
                background: '#d97706', color: '#fff', fontWeight: 700,
                cursor: 'pointer', fontSize: 14,
              }}
            >
              Acknowledged — Continue
            </button>
          </div>
        </div>
      )}

      {/* ── Wrong-document rejection popup ───────────────────────────────── */}
      {docErrorPopup && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.55)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '36px 36px 28px',
            maxWidth: 460, width: '92%',
            boxShadow: '0 16px 48px rgba(0,0,0,0.25)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12, lineHeight: 1 }}>❌</div>
            <h3 style={{ margin: '0 0 6px', fontSize: 19, color: '#dc2626', fontWeight: 700 }}>
              Wrong Document Uploaded
            </h3>
            <p style={{ margin: '0 0 16px', color: '#0f172a', fontSize: 15, fontWeight: 600 }}>
              {docErrorPopup.label} slot rejected this file.
            </p>
            <p style={{
              margin: '0 0 24px', color: '#64748b', fontSize: 14,
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 8, padding: '12px 16px', lineHeight: 1.6,
            }}>
              {docErrorPopup.message}
            </p>
            <button
              onClick={() => setDocErrorPopup(null)}
              style={{
                padding: '11px 36px', borderRadius: 8, border: 'none',
                background: '#dc2626', color: '#fff', fontWeight: 700,
                cursor: 'pointer', fontSize: 14, letterSpacing: 0.2,
              }}
            >
              OK — Upload the Correct Document
            </button>
          </div>
        </div>
      )}

      {/* ── Module header ── */}
      <div className="m3-header">
        <div className="m3-badge">Module 2</div>
        <div>
          <h1 className="m3-title">Deal Intake</h1>
          <p className="m3-subtitle">Upload &amp; Centralise Deal Data</p>
        </div>
        <div className="m3-deal-pill">
          <span className="m3-deal-pill-label">Active Deal</span>
          <span className="m3-deal-pill-name">{dealState.dealName || '—'}</span>
        </div>
      </div>

      {/* ── Two-column body ── */}
      <div className="m3-body">

        {/* LEFT — Upload files */}
        <div className="m3-panel">
          <div className="m3-panel-header">
            <span className="m3-panel-icon">📁</span>
            <h2>Upload Files</h2>
          </div>
          <p className="m3-panel-desc">Drag &amp; drop or click each card to upload the corresponding document.</p>

          {/* OM agent-gate indicator */}
          {!files.om ? (
            <div className="m3-om-gate-banner">
              <span>📄</span>
              <div>
                <strong>OM upload required for AI agent</strong>
                <p>The autonomous agent cannot extract financials or advance past Module 2 until the Offering Memorandum is uploaded. Upload the OM PDF first.</p>
              </div>
            </div>
          ) : (
            <div className="m3-om-gate-banner confirmed">
              <span></span>
              <div>
                <strong>OM uploaded — agent gate cleared</strong>
                <p>{files.om?.name || dealState.financialDocuments?.omFileName || 'Offering Memorandum'} · The AI agent can now extract financials and continue the pipeline.</p>
              </div>
            </div>
          )}

          <div className="m3-doc-grid">
            {DOC_TYPES.map(doc => (
              <FileCard
                key={doc.id}
                doc={doc}
                file={files[doc.id]}
                locked={doc.id === 'om' && omLocked}
                link={listingLink}
                onFile={f => setFile(doc.id, f)}
                onRemove={() => removeFile(doc.id)}
                onLink={setListingLink}
              />
            ))}
          </div>

          <div className="m3-upload-summary">
            {Object.values(files).filter(Boolean).length} of 4 documents uploaded
            {listingLink && ' · Listing link added'}
          </div>
          {errors.om  && <div className="m3-om-required-error">{errors.om}</div>}
          {errors.t12 && <div className="m3-om-required-error">{errors.t12}</div>}

          {Object.entries(parseStatus).filter(([, status]) => status).length > 0 && (
            <div className="m3-parse-panel">
              <div className="m3-parse-title">
                <span>Document extraction</span>
                <button type="button" onClick={() => setExtractionDrawerOpen(true)}>Review log</button>
              </div>
              {Object.entries(parseStatus).filter(([, status]) => status).map(([id, status]) => {
                const extracted = Object.entries(status.fields || {}).filter(([, value]) => value !== '' && value != null);
                return (
                  <div key={id} className={`m3-parse-row ${status.state}`}>
                    <div>
                      <strong>{DOC_TYPES.find(doc => doc.id === id)?.label || id}</strong>
                      <p>{status.message}</p>
                      {extracted.length > 0 && (
                        <div className="m3-extracted-fields">
                          {extracted.slice(0, 12).map(([field, value]) => (
                            <span key={field}>
                              <b>{PARSED_FIELD_LABELS[field] || field}</b>
                              {String(value)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <span>
                      {status.state === 'loading' ? 'Reading…'
                        : status.state === 'invalid' ? '⚠ Wrong doc'
                          : `${extracted.length} fields`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="m3-readiness-panel">
            <div>
              <span>Intake Readiness</span>
              <strong>{intakeReadiness.label}</strong>
            </div>
            <div className="m3-readiness-grid">
              <div><span>Required gaps</span><strong>{intakeReadiness.requiredMissing}</strong></div>
              <div><span>Doc gaps</span><strong>{intakeReadiness.docMissing}</strong></div>
              <div><span>Quality</span><strong>{dataQuality.pct}%</strong></div>
            </div>
            <div className="m3-next-actions">
              {intakeReadiness.nextActions.length
                ? intakeReadiness.nextActions.map(action => <b key={action}>{action}</b>)
                : <b>Ready for underwriting handoff</b>}
            </div>
          </div>

          {/* ── Feature 1: Data Quality Score panel ── */}
          <div className={`m3-quality-panel m3-quality-${dataQuality.level}`}>
            <div className="m3-quality-header">
              <span className="m3-quality-title">📊 Data Quality Score</span>
              <span className={`m3-quality-badge m3-quality-badge-${dataQuality.level}`}>
                {dataQuality.pct}% — {dataQuality.level.charAt(0).toUpperCase() + dataQuality.level.slice(1)}
              </span>
            </div>
            <div className="m3-quality-bar-track">
              <div className="m3-quality-bar-fill" style={{ width: `${dataQuality.pct}%` }} />
            </div>
            <div className="m3-quality-checks">
              {['required', 'financial', 'broker', 'document'].map(cat => {
                const items = dataQuality.checks.filter(c => c.cat === cat);
                const catLabel = { required: 'Required Fields', financial: 'Financial Data', broker: 'Broker Info', document: 'Documents' }[cat];
                return (
                  <div key={cat} className="m3-quality-cat">
                    <div className="m3-quality-cat-label">{catLabel}</div>
                    <div className="m3-quality-cat-items">
                      {items.map(c => (
                        <span key={c.key} className={`m3-quality-item ${c.done ? 'done' : 'missing'}`}>
                          {c.done ? '✓' : '!'} {c.label}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="m3-quality-footer">
              {dataQuality.done} of {dataQuality.total} fields complete
              {dataQuality.pct < 70 && <span className="m3-quality-tip"> — fill missing fields for best underwriting results</span>}
            </div>
          </div>
        </div>

        {/* RIGHT — Deal information */}
        <div className="m3-panel">
          <div className="m3-panel-header">
            <span className="m3-panel-icon">🏢</span>
            <h2>Deal Information</h2>
          </div>
          <p className="m3-panel-desc">Confirm or fill in the property details below.</p>

          {/* Asking price missing banner */}
          {omLocked && !form.askingPrice && (
            <div style={{
              background: '#fffbeb', border: '1px solid #f59e0b',
              borderRadius: 8, padding: '12px 16px', marginBottom: 16,
              display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: 18, lineHeight: 1.2 }}>⚠️</span>
              <div>
                <strong style={{ color: '#92400e', fontSize: 14 }}>Asking Price not found in OM</strong>
                <p style={{ margin: '4px 0 0', color: '#78350f', fontSize: 13, lineHeight: 1.5 }}>
                  The OM did not contain a clear asking price. You can still advance to underwriting —
                  enter the price below if you have it, or leave it blank and update it in the
                  Underwriting Engine. <em>Do not change other financial fields</em> — they were
                  extracted from your uploaded documents.
                </p>
              </div>
            </div>
          )}

          <div className="m3-form">

            {/* Property name */}
            <div className="m3-field m3-field-full">
              <FieldLabel required sources={sourcesFor('propertyName')}>Property Name</FieldLabel>
              <input className={`m3-input ${errors.propertyName ? 'err' : ''} ${extractedFieldClass('propertyName')}`} value={form.propertyName}
                onChange={e => setField('propertyName', e.target.value)} placeholder="" />
              {errors.propertyName && <span className="m3-err">{errors.propertyName}</span>}
            </div>

            {/* Address */}
            <div className="m3-field m3-field-full">
              <FieldLabel required sources={sourcesFor('address')}>Address</FieldLabel>
              <input className={`m3-input ${errors.address ? 'err' : ''} ${extractedFieldClass('address')}`} value={form.address}
                onChange={e => setField('address', e.target.value)} placeholder="" />
              {errors.address && <span className="m3-err">{errors.address}</span>}
            </div>

            {/* City / State / ZIP */}
            <div className="m3-field-row">
              <div className="m3-field">
                <FieldLabel sources={sourcesFor('city')}>City</FieldLabel>
                <input className={`m3-input ${extractedFieldClass('city')}`} value={form.city} onChange={e => setField('city', e.target.value)} placeholder="" />
              </div>
              <div className="m3-field m3-field-sm">
                <FieldLabel sources={sourcesFor('state')}>State</FieldLabel>
                <input className={`m3-input ${extractedFieldClass('state')}`} maxLength={2} value={form.state} onChange={e => setField('state', e.target.value)} placeholder="" />
              </div>
              <div className="m3-field m3-field-sm">
                <FieldLabel sources={sourcesFor('zip')}>ZIP</FieldLabel>
                <input className={`m3-input ${extractedFieldClass('zip')}`} value={form.zip} onChange={e => setField('zip', e.target.value)} placeholder="" />
              </div>
            </div>

            {/* Units / Asset type */}
            <div className="m3-field-row">
              <div className="m3-field">
                <FieldLabel required sources={sourcesFor('units')}>Units</FieldLabel>
                <input type="number" className={`m3-input ${errors.units ? 'err' : ''} ${extractedFieldClass('units')}`} value={form.units}
                  onChange={e => setField('units', e.target.value)} placeholder="" />
                {errors.units && <span className="m3-err">{errors.units}</span>}
              </div>
              <div className="m3-field">
                <FieldLabel sources={sourcesFor('assetType')}>Asset Type</FieldLabel>
                <select className="m3-input" value={form.assetType} onChange={e => setField('assetType', e.target.value)}>
                  {ASSET_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>

            {/* Asking price / Year built */}
            <div className="m3-field-row">
              <div className="m3-field">
                <FieldLabel sources={sourcesFor('askingPrice')}>Asking Price ($)</FieldLabel>
                {!form.askingPrice && (
                  <span className="m3-est-tag" style={{ color: '#d97706', marginTop: -3 }}>
                    Not found in OM, enter manually
                  </span>
                )}
                <input type="number" className={`m3-input ${extractedFieldClass('askingPrice')}`} value={form.askingPrice}
                  onChange={e => setField('askingPrice', e.target.value)} placeholder="Enter asking price (optional)" />
              </div>
              <div className="m3-field">
                <FieldLabel sources={sourcesFor('yearBuilt')}>Year Built</FieldLabel>
                <input type="number" className={`m3-input ${extractedFieldClass('yearBuilt')}`} value={form.yearBuilt}
                  onChange={e => setField('yearBuilt', e.target.value)} placeholder="" />
              </div>
            </div>

            {/* Occupancy */}
            <div className="m3-field">
              <FieldLabel sources={sourcesFor('occupancy')}>Current Occupancy (%)</FieldLabel>
              <input type="number" className={`m3-input ${extractedFieldClass('occupancy')}`} min="0" max="100" value={form.occupancy}
                onChange={e => setField('occupancy', e.target.value)} placeholder="" />
            </div>

            {/* Broker info */}
            <div className="m3-section-label">Broker / Contact</div>
            <div className="m3-field-row">
              <div className="m3-field">
                <FieldLabel sources={sourcesFor('brokerName')}>Broker Name</FieldLabel>
                <input className={`m3-input ${extractedFieldClass('brokerName')}`} value={form.brokerName}
                  onChange={e => setField('brokerName', e.target.value)} placeholder="" />
              </div>
              <div className="m3-field">
                <FieldLabel sources={sourcesFor('brokerPhone')}>Phone</FieldLabel>
                <input className={`m3-input ${extractedFieldClass('brokerPhone')}`} value={form.brokerPhone}
                  onChange={e => setField('brokerPhone', e.target.value)} placeholder="" />
              </div>
            </div>
            <div className="m3-field m3-field-full">
              <FieldLabel sources={sourcesFor('brokerEmail')}>Broker Email</FieldLabel>
              <input type="email" className={`m3-input ${extractedFieldClass('brokerEmail')}`} value={form.brokerEmail}
                onChange={e => setField('brokerEmail', e.target.value)} placeholder="" />
            </div>

            {/* Financials section */}
            <div className="m3-section-label">Financials (T12 Summary)</div>

            <div className="m3-field-row">
              <div className="m3-field">
                <FieldLabel sources={sourcesFor('grossRent')}>Monthly Gross Rent ($)</FieldLabel>
                <input type="number" className={`m3-input ${extractedFieldClass('grossRent')}`} value={form.grossRent}
                  onChange={e => handleFinancialChange('grossRent', e.target.value)} placeholder="" />
              </div>
              <div className="m3-field">
                <FieldLabel sources={sourcesFor('otherIncome')}>Other Income ($/mo)</FieldLabel>
                <input type="number" className={`m3-input ${extractedFieldClass('otherIncome')}`} value={form.otherIncome}
                  onChange={e => handleFinancialChange('otherIncome', e.target.value)} placeholder="" />
              </div>
            </div>
            <div className="m3-field-row">
              <div className="m3-field">
                <FieldLabel sources={sourcesFor('vacancyRate')}>Vacancy Rate (%)</FieldLabel>
                <input type="number" className={`m3-input ${extractedFieldClass('vacancyRate')}`} value={form.vacancyRate}
                  onChange={e => handleFinancialChange('vacancyRate', e.target.value)} placeholder="" />
              </div>
              <div className="m3-field">
                <FieldLabel sources={sourcesFor('insurance')} fallback={form.askingPrice ? 'Estimated from asking price because no insurance line was extracted' : undefined}>
                  Annual Insurance ($)
                </FieldLabel>
                {!form.insurance && form.askingPrice && (
                  <span className="m3-est-tag" style={{ marginTop: -3 }}>Estimated from price</span>
                )}
                <input
                  type="number"
                  className={`m3-input ${extractedFieldClass('insurance')}`}
                  value={form.insurance}
                  onChange={e => setField('insurance', e.target.value)}
                  placeholder={form.askingPrice ? String(estimateInsurance(form.askingPrice)) : ''}
                />
              </div>
            </div>
            <div className="m3-field-row">
              <div className="m3-field">
                <FieldLabel sources={sourcesFor('opExpenses')}>Annual Op. Expenses ($) <span className="m3-field-note">excl. insurance</span></FieldLabel>
                <input type="number" className={`m3-input ${extractedFieldClass('opExpenses')}`} value={form.opExpenses}
                  onChange={e => handleFinancialChange('opExpenses', e.target.value)} placeholder="" />
              </div>
              <div className="m3-field">
                <FieldLabel sources={sourcesFor('opExpenses')} fallback="Calculated from annual operating expenses divided by 12">Monthly Op. Expenses</FieldLabel>
                <div className="m3-derived-value">
                  {form.opExpenses ? `$${Math.round(Number(form.opExpenses) / 12).toLocaleString()}/mo` : '—'}
                </div>
              </div>
            </div>

            {/* Total Monthly Cost Preview */}
            {(form.opExpenses || form.insurance || form.askingPrice) && (
              <div className="m3-monthly-cost-preview">
                <div className="m3-mcp-title">Monthly Cost Estimate</div>
                <div className="m3-mcp-row">
                  <span>Insurance</span>
                  <span>
                    {form.insurance
                      ? `$${Math.round(Number(form.insurance) / 12).toLocaleString()}/mo`
                      : form.askingPrice
                        ? `$${Math.round(estimateInsurance(form.askingPrice) / 12).toLocaleString()}/mo (est.)`
                        : '—'
                    }
                  </span>
                </div>
                <div className="m3-mcp-row">
                  <span>Operating Expenses</span>
                  <span>{form.opExpenses ? `$${Math.round(Number(form.opExpenses) / 12).toLocaleString()}/mo` : '—'}</span>
                </div>
              </div>
            )}

            <div className="m3-noi-display">
              <span className="m3-noi-label">
                {extractedNOI ? 'NOI (from document)' : 'Calculated NOI'}
                <FieldSourceHint
                  sources={sourcesFor('noi')}
                  fallback="Calculated from gross rent, other income, vacancy, and operating expenses"
                />
              </span>
              <strong>{displayedNOI ? `$${Number(displayedNOI).toLocaleString()}` : '—'}</strong>
            </div>
            {extractedNOI && (
              <p style={{ margin: '4px 0 12px', fontSize: 12, color: '#64748b' }}>
                NOI is locked to the value extracted from your uploaded documents.
                It will not change when you edit other fields.
              </p>
            )}

          </div>

          {/* ── 2-Minute Analysis ── */}
          <div className="m3-two-minute-section">
            <TwoMinuteAnalysis />
          </div>

        </div>

      </div>{/* end .m3-body */}

      {extractionReview.hasLogs && (
        <>
          {!extractionDrawerOpen && (
            <button
              type="button"
              className="m3-extraction-fab"
              onClick={() => setExtractionDrawerOpen(true)}
            >
              Extraction Log
            </button>
          )}
          <aside className={`m3-extraction-drawer ${extractionDrawerOpen ? 'open' : ''}`} aria-label="Extraction review log">
            <div className="m3-extraction-drawer-header">
              <div>
                <span>PDF to MVP Review</span>
                <strong>Extracted fields and calculated outputs</strong>
              </div>
              <button type="button" onClick={() => setExtractionDrawerOpen(false)} aria-label="Close extraction review">×</button>
            </div>

            {isProcessing && (
              <div className="m3-log-processing">
                <b>Reading document</b>
                <span>{processingMessage || 'Parsing file and mapping deal terms...'}</span>
              </div>
            )}

            <div className="m3-log-section">
              <h3>Extraction Steps</h3>
              {extractionReview.docs.map(doc => (
                <div key={doc.id} className={`m3-log-doc ${doc.state}`}>
                  <div className="m3-log-doc-head">
                    <strong>{doc.label}</strong>
                    <span>{doc.fileName || doc.state}</span>
                  </div>
                  <p>{doc.message}</p>
                  <ol>
                    {(parseStatus[doc.id]?.steps || []).map(step => <li key={step}>{step}</li>)}
                  </ol>
                </div>
              ))}
            </div>

            <div className="m3-log-section">
              <h3>Just Extracted</h3>
              {extractionReview.docs.flatMap(doc => doc.fields.map(([field, value]) => ({ doc, field, value }))).length ? (
                <div className="m3-log-field-list">
                  {extractionReview.docs.flatMap(doc => doc.fields.map(([field, value]) => ({ doc, field, value }))).map(({ doc, field, value }) => (
                    <div key={`${doc.id}-${field}`}>
                      <span>{PARSED_FIELD_LABELS[field] || field}<small>{doc.label}</small></span>
                      <b>{formatReviewValue(value, field)}</b>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="m3-log-muted">No mapped fields yet. Upload a searchable PDF, XLSX, or text file.</p>
              )}
            </div>

            <div className="m3-log-section">
              <h3>Being Calculated</h3>
              <div className="m3-log-field-list calculated">
                {extractionReview.calculated.map(([label, value]) => (
                  <div key={label}>
                    <span>{label}<small>SDA formula output</small></span>
                    <b>{formatReviewValue(value)}</b>
                  </div>
                ))}
              </div>
            </div>

            <div className="m3-log-section">
              <h3>Highlighted Source Terms</h3>
              {extractionReview.docs.map(doc => (
                <div key={`${doc.id}-preview`} className="m3-log-preview">
                  <strong>{doc.label} source preview</strong>
                  <p>{buildHighlightedPreview(doc.textPreview, extractionReview.allTerms)}</p>
                </div>
              ))}
            </div>
          </aside>
        </>
      )}

      <div className="module-bottom-advance">
        <button
          className="m3-submit-btn"
          onClick={handleSubmit}
        >
          Complete Deal Intake &amp; Advance to Deal Score
        </button>
      </div>
    </div>
  );
}

export default Module3Page;
